import { Model, Sector } from '@arra/core';
import styled from '@emotion/styled';
import { Button, Tooltip } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';

import HistoryIcon from '@mui/icons-material/History';
import MostImpactfulActions from '../actions/card/MostImpactfulActions';
import ResultsSimple from '../results/ResultsSimple';
import { getProjectTitle, useProject } from './context/useProject';
import Nav from './Nav';
import Parameters from './Parameters';

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import useUser from '../authentication/context/useUser';
import { HeaderButton, IconContainer } from '../layout/Header';
import BackToComponent from './BackToComponent';
import useBackTo from './context/useBackTo';
import { useValidationContext } from './context/useValidation';
import FinalisationSection from './FinalisationSection';
import ModalVersionUpdate from './ModalVersionUpdate';
import keyify from './parameters/keyify';
import Loader from '../ui/Loader';

interface SimulatorOptions {
  secondaryNav: boolean;
}

const simulatorOptions = {
  secondaryNav: false,
};

const Container = styled.div`
    width: 100%;
    background-color: var(--backgroundColor);
    display: flex;
    gap: 16px;
    padding: 0 16px;
  }
`;

type SectorComponentWrapperProps = {
  depth: number;
};

const SectorComponentWrapper = styled.div`
  margin-bottom: ${(props: SectorComponentWrapperProps) =>
    props.depth === 0 ? '16px' : '0px'};
  background-color: white;
`;

const LeftColumn = styled.div`
  width: min(65%, calc(100% - 400px));
  border-radius: 8px;
  position: sticky;
  top: 64px;
`;

const RightColumn = styled.div`
  width: max(35%, 400px);
  height: calc(100vh - 96px);
  position: sticky;
  display: flex;
  flex-direction: column;
  gap: 8px;
  top: 80px;
`;

const SectorWrapper = styled.div`
  width: 100%;
  background-color: white;
  padding: 0 16px;
`;

// HeaderContainer is necessary to mask the parameters when scrolling
const HeaderContainer = styled.div`
  background-color: var(--backgroundColor);
  padding-top: 16px;
  position: sticky;
  top: 64px;
  z-index: 120;
`;

// NavContainer is used to display the margin between Nav and Parameters
const NavContainer = styled.div`
  background-color: white;
  border-radius: 8px 8px 0 0;
`;

const MainContainer = styled.div`
  padding: 0;
  background-color: var(--backgroundColor);
`;

interface FinalisationContainerProps {
  bgColor?: string;
}

const FinalisationContainer = styled.div<FinalisationContainerProps>`
  background-color: ${(props) => props.bgColor || 'var(--EGGSHELL)'};
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 8px;
  padding: 16px;
  z-index: 1300;
`;

const VersionningButton = ({
  onClick,
  nodeSelector,
}: {
  onClick?: () => void;
  nodeSelector: string;
}) => {
  const element = document.querySelector(nodeSelector);
  if (!element) return null;
  return ReactDOM.createPortal(
    <Tooltip title="Historique du modèle">
      <HeaderButton onClick={onClick}>
        <IconContainer>
          <HistoryIcon />
        </IconContainer>
        <p className="h6r">Historique</p>
      </HeaderButton>
    </Tooltip>,
    element,
  );
};
export const ProjectForm = () => {
  const [openModalVersionUpdate, setOpenModalVersionUpdate] = useState(false);
  const { projectId } = useParams();
  const { project, loading, updateLastSeenAt } = useProject(projectId);
  const { t } = useTranslation(['project', 'model']);
  const { state: backToState, setState: setBackTo } = useBackTo();
  const { user } = useUser();
  const location = useLocation();

  const { isValidationVisible } = useValidationContext();
  const [lastSeenAt, setLastSeenAt] = useState<Date | undefined>();

  useEffect(() => {
    if (loading || !project) {
      return;
    }

    const userProject = project?.users.find(
      (u) => u.id.toString() === user?._id.toString(),
    );

    const seen = !userProject
      ? new Date(2900, 0, 0)
      : userProject?.lastSeenAt
      ? new Date(userProject?.lastSeenAt)
      : new Date(1900, 0, 0);
    const published = project?.model.publishedAt
      ? new Date(project.model.publishedAt)
      : new Date(1901, 0, 0);
    if (!seen || seen < published) {
      setLastSeenAt(seen);
      setOpenModalVersionUpdate(true);
    }
  }, [
    lastSeenAt,
    loading,
    location,
    project,
    project?.model.publishedAt,
    project?.users,
    user?._id,
  ]);

  useEffect(() => {
    const scrollTo = (location.state as any)?.scrollTo;
    if (scrollTo) {
      const element = document.querySelector(scrollTo);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'auto' });
        }, 100);
      }
    }
  }, [location]);

  useEffect(() => {
    if (!projectId) return;
    if (backToState && !backToState?.value) {
      setBackTo({
        ...backToState,
        value: projectId,
      });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (!project) return <Loader fullPage />;

  const projectTitle = getProjectTitle(
    project,
    t(`model:${keyify(project?.model.name)}.defaultTitle`),
  );

  const handleModalVersionUpdateClosed = () => {
    setOpenModalVersionUpdate(false);
    updateLastSeenAt();
  };

  return (
    <Container data-tour="project-form">
      <VersionningButton
        onClick={() => setOpenModalVersionUpdate(true)}
        nodeSelector="#action-container"
      />
      <ModalVersionUpdate
        open={openModalVersionUpdate}
        onClose={handleModalVersionUpdateClosed}
        project={project}
        lastSeenAt={lastSeenAt}
      />
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`${projectTitle} - TRANSITION ENVIRONNEMENTALE DE LA FILIÈRE DU JEU VIDÉO`}</title>
      </Helmet>
      <LeftColumn>
        <HeaderContainer>
          <NavContainer>
            {backToState && backToState.value === projectId && (
              <BackToComponent state={backToState} />
            )}
            <Nav />
          </NavContainer>
        </HeaderContainer>
        <MainContainer>
          <SectorWrapper>
            <SectorComponent
              sectors={project?.sectors}
              depth={0}
              upperSector={null}
              options={simulatorOptions}
              isValidationVisible={isValidationVisible}
            />
          </SectorWrapper>
        </MainContainer>
      </LeftColumn>
      <RightColumn>
        <FinalisationContainer bgColor="var(--TEAROSE)">
          <FinalisationSection project={project} />
        </FinalisationContainer>
        <ResultsSimple
          results={project?.results}
          model={project?.model}
          loading={loading}
        />
        {project.model.config.actions && (
          <MostImpactfulActions actions={project?.actions} />
        )}
      </RightColumn>
    </Container>
  );
};

export const ScrollToTopOnce = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return null;
};

const ScrollHandleContainer = styled.div`
  position: relative;
  top: -200px;
`;

export const ScrollHandle = React.forwardRef<HTMLDivElement, any>(
  (props, ref) => {
    const [top, setTop] = useState(0);

    useEffect(() => {
      const categoriesWrapper = document.querySelector('.categories_wrapper');
      if (!categoriesWrapper) return;
      const bottom = categoriesWrapper.getBoundingClientRect().bottom;
      setTop(bottom + (props.offset || 0) + 20);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <ScrollHandleContainer
        {...props}
        ref={ref}
        style={{ top: -top }}
      ></ScrollHandleContainer>
    );
  },
);

const SectorComponent = ({
  sectors,
  depth,
  upperSector,
  options,
  isValidationVisible,
}: {
  sectors?: Sector[];
  depth: number;
  upperSector: Sector | null;
  options: SimulatorOptions;
  isValidationVisible: boolean;
}) => {
  const { projectId } = useParams();
  const { project, updateParameter } = useProject(projectId);

  if (!project) {
    return null;
  }

  const getSectorId = (
    sector: Sector,
    upperSector: Sector | null,
    depth: number,
  ) => {
    return upperSector && depth > 0
      ? `${keyify(upperSector.information.name)}-${keyify(
          sector.information.name,
        )}-${depth}`
      : `${keyify(sector.information.name)}-${depth}`;
  };

  return (
    <>
      {(sectors || []).map((sector, index) => {
        const displayHeader =
          sector.parameters.length > 0 || sector.sectors.length > 0;
        const sectorId = getSectorId(sector, upperSector, depth);

        return (
          <SectorComponentWrapper key={`${sectorId}-${index}`} depth={depth}>
            <ScrollHandle id={sectorId}></ScrollHandle>
            {displayHeader && (
              <SectorHeader
                upperSector={upperSector}
                sector={sector}
                depth={depth}
                information={sector.information}
                subsectors={sector.sectors}
                sectorId={sectorId}
                options={options}
                externalModules={
                  project.model.config.parameters.externalModules
                }
                model={project.model}
              />
            )}
            {sector.parameters.length > 0 && (
              <Parameters
                autofocusFirst={false}
                parameters={sector.parameters}
                onUpdateParameter={updateParameter}
                model={project.model}
                isValidationVisible={isValidationVisible}
                project={project}
                isCreateProject={false}
              />
            )}
            {sector.sectors.length > 0 && (
              <SectorComponent
                sectors={sector.sectors}
                depth={depth + 1}
                upperSector={sector}
                options={options}
                isValidationVisible={isValidationVisible}
              />
            )}
          </SectorComponentWrapper>
        );
      })}
    </>
  );
};

type SectorHeaderWrapperProps = {
  backgroundColor: string;
};

export const SectorHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 16px;
  color: black;
  justify-content: space-between;
  &.level0 {
    top: 174px;
    position: sticky;
    padding: 14px 16px;
    border-radius: 4px;
    background-color: ${(props: SectorHeaderWrapperProps) =>
      props.backgroundColor || 'lightgrey'};
    z-index: 110;
  }
  &.level0 p {
    color: ${(props: SectorHeaderWrapperProps) =>
      contrastColor(props.backgroundColor) || 'black'};
    font-family: 'Lexend Exa', sans-serif !important;
  }
  &.level1 {
    width: 100%;
    padding: 16px 0 16px 20px;
    top: 227px;
    background-color: white;
    position: sticky;
    z-index: 100;
  }
  &.level1 p {
    line-height: 18px;
    font-family: 'Lexend Exa', sans-serif !important;
  }
  &.level1 p::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 4px;
    background-color: ${(props: SectorHeaderWrapperProps) =>
      props.backgroundColor || 'lightgrey'};
    left: 0;
    display: block;
    line-height: 18px;
    margin-top: 1px;
  }
  &.level2 p {
    font-style: italic;
    font-family: 'Lexend Exa', sans-serif !important;
  }
`;

const headerClassName: { [key: number]: string } = {
  0: 'h4b',
  1: 'h5b',
  2: 'hxb',
};

const CategoriesWrapper = styled.div`
  display: flex;
`;

interface SectorHeaderProps {
  sector: Sector;
  upperSector: Sector | null;
  depth: number;
  sectorId: string;
  information: {
    color: string;
    name: string;
  };
  subsectors: Sector[];
  options: SimulatorOptions;
  externalModules?: {
    [key: string]: string[];
  };
  model: Model;
}

// https://stackoverflow.com/a/72595895/1665540
const contrastColor = (backgroundColor: string) => {
  try {
    return ['black', 'white'][
      ~~(
        [1, 3, 5]
          .map((p) => parseInt(backgroundColor.substr(p, 2), 16))
          .reduce((r, v, i) => [0.299, 0.587, 0.114][i] * v + r, 0) < 128
      )
    ];
  } catch (e) {
    return 'black';
  }
};

const SectorHeader = ({
  sector,
  upperSector,
  depth,
  information,
  sectorId,
  subsectors,
  options,
  externalModules,
  model,
}: SectorHeaderProps) => {
  const { t } = useTranslation(['model']);
  const { color, name } = information;
  const navigation = subsectors.map((subsector) => ({
    name: subsector.information.name,
    key: `${sectorId}-${subsector.information.name}--${depth + 1}`,
    color: subsector.information.color,
  }));

  const translationPrefix = `model:${keyify(model.name)}`;

  const handleButtonClicked = (hash: string) => () => {
    document?.getElementById(hash)?.scrollIntoView();
  };

  return (
    <>
      <SectorHeaderWrapper backgroundColor={color} className={`level${depth}`}>
        <p className={headerClassName[depth]}>
          {t(`${translationPrefix}.section.level-${depth}.${keyify(name)}`)}
        </p>
        {depth === 0 && options.secondaryNav && navigation.length > 0 && (
          <CategoriesWrapper className="categories_wrapper">
            {navigation.map(({ name, key, color }) => (
              <Button
                key={key}
                onClick={handleButtonClicked(key)}
                size="small"
                sx={{
                  '&:hover': {
                    bgcolor: color,
                    color: contrastColor(color || '#ffffff'),
                  },
                }}
              >
                {t(
                  `${translationPrefix}.section.level-${depth}.${keyify(name)}`,
                )}
              </Button>
            ))}
          </CategoriesWrapper>
        )}
      </SectorHeaderWrapper>
    </>
  );
};
